import { Component, OnDestroy, OnInit } from "@angular/core";
import { SubscriptionService } from "../../../subscription/subscription.service";
import { ActivatedRoute, Router } from "@angular/router";
import { BuyService } from "../../../buy/buy.service";
import { Model } from "../../../app.model";
import { HttpClient } from "@angular/common/http";
import { CompanyService } from "../../../company/company.service";
import { WorkflowEndcustomerService } from "../workflow-endcustomer.service";
import { firstValueFrom } from "rxjs";
import { PaymentDetails, SaleOrder } from "src/app/buy/buy.model";
import { runInAction } from 'mobx';
import { BaseWorkflowModel, WorkflowEndcustomerComponent } from "../workflow-endcustomer.component";
import { AvailabilityProduct, AvailabilityResponse } from "../../workflow.types";

type WorkflowModel = BaseWorkflowModel & {
    availability: string;
    availabilityLevels: AvailabilityProduct[];
}

@Component({
    selector: 'app-workflow-endcustomer-one-availability',
    templateUrl: './workflow-endcustomer-one-availability.component.html',
    standalone: false
})
export class WorkflowEndcustomerOneAvailabilityComponent extends WorkflowEndcustomerComponent<WorkflowModel> implements OnInit, OnDestroy {
    constructor(protected route: ActivatedRoute,
        protected router: Router,
        protected subscriptionService: SubscriptionService,
        protected buyService: BuyService,
        protected m: Model,
        protected http: HttpClient,
        protected workflowEndcustomerService: WorkflowEndcustomerService,
        protected companyService: CompanyService) {
        super(route, router, subscriptionService, buyService, m, http, workflowEndcustomerService, companyService);
    }

    async ngOnInit() {
        await super.ngOnInit();

        if (!this.checkSubscriptionValid) {
            this.resetWorkflow();
            return;
        }

        if (this.availabilityOrder) {
            this.resetWorkflow();
            return;
        }

        if (this.model.subscription.isAvailabilityBeta) {
            this.step = 'contact-sales';
            this.navigate(['contact-sales']);
            return;
        }

        const result = await firstValueFrom(
            this.http.get<AvailabilityResponse>('/services/products/cloud')
        );

        runInAction(() => {
            this.model.availabilityLevels = (result.availability || []).map(t => ({ name: t.name, list_price: t.list_price }));
            this.model.availabilityLevels.push({ name: 'STANDARD', list_price: 0 });
            this.model.availability = this.model.subscription.availability;
        });

        this.loadCompany();

        this.initialized = true;
    }

     ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    get workflowName(): string {
        return 'availability';
    }

    public onPremiumSelected() {
        this.navigate(['address']);
    }
    
    public onStandardSelected() {
        this.navigate(['downgrade']);
    }

    public onAvailabilityBack() {
        this.resetWorkflow();
    }

    public async onDowngradeSelected() {
        const saleOrder = await firstValueFrom(this.workflowEndcustomerService.oneApplianceDowngradePremium(this.applianceId))
        runInAction(() => {
            this.model.saleOrder = saleOrder;
            this.m.buy.currentOrder = saleOrder;
        });
        
        await firstValueFrom(this.buyService.confirmUpgrade());
        this.navigate(['complete']);
    }

    public onDowngradeBack() {
        this.resetWorkflow();
    }

    public onInvoiceAddressBack() {
        this.navigate(['select']);
    }

    public async onInvoiceNext(e: any) {
        if (this.isPaymentStepNeeded) {
            this.navigate(['payment']);
            return;
        }

        await this.checkout();
    }

    protected onSummaryBack() {
        if (this.isPaymentStepNeeded) {
            this.navigate(['payment']);
            return;
        }

        this.onPaymentMethodBack();
    }

    private async checkout() {
        await this.createOffer();
        await firstValueFrom(this.buyService.getOfferRecalculate(this.model.saleOrder.name));
        this.navigate(['checkout']);
    }

    private async createOffer() {
        if (this.model.saleOrder?.name) return;

        const saleOrder = await firstValueFrom(this.workflowEndcustomerService.oneApplianceAddPremium(this.applianceId));
        runInAction(() => {
            this.model.saleOrder = saleOrder;
        })
    }

    public async onPaymentMethod(payment: PaymentDetails) {
        await this.setPaymentDetails(payment);
        await this.checkout();
    }

    public onPaymentMethodBack() {
        this.navigate(['address']);
    }

    public onBuy() {
        this.buyService.confirmUpgrade().subscribe(
            (saleOrder: SaleOrder) => {
                this.navigate(['complete']);
            }
        );
    }

    private get availabilityOrder(): SaleOrder {
        return this.model.subscription.orders.find(order =>
            order.lines.find(line =>
                ['PC-ONE-PREMIUM', 'PC-ONE-CANCEL-PREMIUM'].includes(line.product)
            )
        );
    }
}
